import { styled } from '@mui/system';
import { useState, useEffect, useCallback } from "react";
import hammer from '../../../assets/icons/hammer.svg';
import forge from '../../../assets/icons/forge.svg';
import linkedin from '../../../assets/icons/linkedin.svg';
import umk from '../../../assets/icons/umk.svg';
import mail from '../../../assets/icons/mail.svg';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
// import { Button } from '../../common/Button/Button';
// import x from '../../../assets/icons/x.svg';

const HammerLogo = styled('img')({
    width: '38px',
    height: '29px',
});

const ForgeLogo = styled('img')({
    width: '67px',
    height: '19px',
});

const LogoContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
});


const HeaderContainer = styled('header')({
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '40px 48px',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    height: '109px',
    zIndex: 1000,
    backgroundColor: '#021a1a',
    '@media (max-width: 1024px)': {
        padding: '20px 24px',
        height: '84px',
    }
});

const NavContainer = styled('nav')<{ isOpen?: boolean }>(({ isOpen = false }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    gap: '47px',
    '@media (max-width: 1024px)': {
        gap: '20px',
    },
    '@media (max-width: 768px)': {
        position: 'fixed',
        top: 0,
        right: isOpen ? '0' : '-100%',
        width: '80%',
        height: '100vh',
        backgroundColor: '#021a1a',
        flexDirection: 'column',
        padding: '80px 20px',
        transition: 'right 0.3s ease-in-out',
        gap: '30px',
    }
}));

const MenuButton = styled('button')({
    display: 'none',
    background: 'none',
    border: 'none',
    color: '#F1F7F7',
    cursor: 'pointer',
    padding: '8px',
    '@media (max-width: 768px)': {
        display: 'block',
    }
});

const Overlay = styled('div')<{ isOpen?: boolean }>(({ isOpen = false }) => ({
    display: 'none',
    '@media (max-width: 768px)': {
        display: isOpen ? 'block' : 'none',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 999,
    }
}));

const NavLink = styled('a')<{ isActive?: boolean }>(({ isActive = false }) => ({
    color: isActive ? '#00E378' : '#F1F7F7',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: isActive ? '700' : '400',
    lineHeight: 'normal',
    textDecoration: 'none',
    position: 'relative',
    cursor: 'pointer',
    fontSynthesis: 'none',
    whiteSpace: 'nowrap',
    transition: 'color 0.3s ease-in-out',

    '&:hover': {
        color: '#00E378',
    }
}));

const TRANSITION_DURATION = 500;
const SCROLL_DEBOUNCE_DELAY = 100;

const ActiveIndicator = styled('div')<{ position: number }>(({ position }) => ({
    position: 'absolute',
    bottom: '0',
    left: '0',
    width: '10px',
    height: '2px',
    backgroundColor: '#00E378',
    transform: `translateX(${position}px)`,
    transition: `transform ${TRANSITION_DURATION}ms ease-in-out`,
    '@media (max-width: 768px)': {
        display: 'none'
    }
}));

const SocialContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
});

const SocialLink = styled('a')({
    textDecoration: 'none',
    color: '#F1F7F7',
    height: '24px',
    maxWidth: '24px',
    cursor: 'pointer',
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.1)',
        transition: 'transform 0.3s ease',
    }
});

const CloseDrawerButton = styled('button')({
    display: 'none',
    background: 'none',
    border: 'none',
    color: '#F1F7F7',
    cursor: 'pointer',
    padding: '8px',
    position: 'absolute',
    top: '20px',
    right: '20px',
    '@media (max-width: 768px)': {
        display: 'block',
    }
});

const navItems = [
    { id: 'home', label: 'header.home' },
    { id: 'product', label: 'header.product' },
    { id: 'devex', label: 'header.devex' },
    { id: 'astrolabe', label: 'header.astrolabe' },
    { id: 'team', label: 'header.team' },
    { id: 'contact', label: 'header.contact' },
];

export const Header = () => {
    const { t } = useTranslation();
    const [activeLink, setActiveLink] = useState('home');
    const [clickedLink, setClickedLink] = useState<string | null>(null);
    const [indicatorPosition, setIndicatorPosition] = useState(0);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isManualScroll, setIsManualScroll] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsTablet(window.innerWidth <= 1024);
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const updateActiveSection = useCallback((currentSection: { id: string }) => {
        if (currentSection && currentSection.id !== activeLink) {
            const activeIndex = navItems.findIndex(item => item.id === currentSection.id);
            const navLinks = document.querySelectorAll('.nav-link');
            if (navLinks[activeIndex]) {
                const linkRect = navLinks[activeIndex].getBoundingClientRect();
                const containerRect = document.querySelector('.nav-container')?.getBoundingClientRect();
                if (containerRect) {
                    setIndicatorPosition(linkRect.left - containerRect.left);
                }
            }
            setActiveLink(currentSection.id);
        }
    }, [activeLink]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout;

        const handleScroll = () => {
            if (isScrolling || isManualScroll) return;

            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                const sections = navItems.map(item => {
                    const element = document.getElementById(item.id);
                    if (element) {
                        const rect = element.getBoundingClientRect();
                        return {
                            id: item.id,
                            top: rect.top,
                            bottom: rect.bottom,
                        };
                    }
                    return null;
                }).filter(Boolean);

                const currentSection = sections.find(section =>
                    section && section.top <= 225 && section.bottom >= 225
                );

                if (currentSection) {
                    updateActiveSection(currentSection);
                }
            }, SCROLL_DEBOUNCE_DELAY);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(timeoutId);
        };
    }, [isScrolling, isManualScroll, updateActiveSection]);

    const handleClick = (id: string) => {
        setIsScrolling(true);
        setIsManualScroll(true);
        setClickedLink(id);
        setIsMenuOpen(false);
        const element = document.getElementById(id);
        if (element) {
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - 120;
            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });

            setTimeout(() => {
                const activeIndex = navItems.findIndex(item => item.id === id);
                const navLinks = document.querySelectorAll('.nav-link');
                if (navLinks[activeIndex]) {
                    const linkRect = navLinks[activeIndex].getBoundingClientRect();
                    const containerRect = document.querySelector('.nav-container')?.getBoundingClientRect();
                    if (containerRect) {
                        setIndicatorPosition(linkRect.left - containerRect.left);
                    }
                }
                setActiveLink(id);
                setClickedLink(null);
                setIsScrolling(false);
                setIsManualScroll(false);
            }, 0);
        }
    };

    return (
        <>
            <Overlay isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
            <HeaderContainer>
                <LogoContainer>
                    <HammerLogo src={hammer} alt="Hammer Logo" />
                    <ForgeLogo src={forge} alt="Forge Logo" />
                </LogoContainer>

                <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    <MenuIcon />
                </MenuButton>

                <NavContainer className="nav-container" isOpen={isMenuOpen}>
                    <CloseDrawerButton onClick={() => setIsMenuOpen(false)}>
                        <CloseIcon />
                    </CloseDrawerButton>

                    {navItems.map((item) => (
                        <NavLink
                            key={item.id}
                            isActive={clickedLink === item.id || (activeLink === item.id && !isScrolling && !isManualScroll)}
                            onClick={() => handleClick(item.id)}
                            className="nav-link"
                            data-id={item.id}
                        >
                            {t(item.label)}
                        </NavLink>
                    ))}
                    <ActiveIndicator position={indicatorPosition} />
                </NavContainer>
                {!isTablet && !isMobile && (
                    <SocialContainer>
                        {/* TODO: Add Log In button */}
                        {/* <Button>Log In</Button> */}

                        <SocialLink href="mailto:contact@forgeit.fr">
                            <img src={mail} alt="Contact" />
                        </SocialLink>
                        <SocialLink onClick={() => window.open('https://www.linkedin.com/company/forgeit-tech/')}>
                            <img src={linkedin} alt="Linkedin" />
                        </SocialLink>
                        {/* TODO: Add X social link */}
                        {/* <SocialLink>
                        <img src={x} alt="X" />
                    </SocialLink> */}
                        <SocialLink onClick={() => window.open('https://www.umake.fr/')}>
                            <img src={umk} alt="Umk" />
                        </SocialLink>

                    </SocialContainer>
                )}
            </HeaderContainer>
        </>
    );
};
